<template>
    <div>
        <div class="flex items-baseline gap-3">
            <jet-datepicker
                :deletable="false"
                :form-group-classes="''"
                :horizontal="false"
                :inline="true"
                :model-value="displayedDate"
                :readonly="readonly"
                @update:model-value="onDateChange"
            />
            <time-field
                :disabled="readonly"
                :model-value="displayedTime"
                root-class="flex items-center text-accent tabular-nums"
                input-class="px-0.5 tabular-nums caret-transparent outline-none data-[placeholder]:text-muted-foreground focus:bg-accent focus:text-white"
                @update:model-value="onTimeChange"
            />
        </div>
        <div v-if="invalidFeedback" class="mt-1 text-[13px] text-danger">{{ invalidFeedback }}</div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import JetDatepicker from '@/Jetstream/Datepicker.vue';
import { TimeField } from '@/Components/ui/time-field';
import { DateTime } from 'luxon';

const props = defineProps({
    modelValue: { type: String, default: null },
    timezone: { type: String, default: 'UTC' },
    readonly: { type: Boolean, default: false },
    invalidFeedback: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

// Today is shown only as a default *display*. The form's modelValue stays null
// (not dirty) until the user actually sets a time — date-only edits are tracked
// in `pickedDate` so the visible selection persists across re-renders.
const todayInZone = computed(() => DateTime.now().setZone(props.timezone).toISODate());
const pickedDate = ref(null);

const modelDateTime = computed(() => {
    if (!props.modelValue) return null;
    const dt = DateTime.fromISO(props.modelValue, { zone: 'utc' });
    return dt.isValid ? dt.setZone(props.timezone) : null;
});

const displayedDate = computed(() => modelDateTime.value?.toISODate() ?? pickedDate.value ?? todayInZone.value);
const displayedTime = computed(() => modelDateTime.value?.toFormat('HH:mm') ?? null);

function emitComposed(date, time) {
    if (!date || !time) {
        return;
    }
    const [hh, mm] = time.split(':').map(Number);
    const composed = DateTime.fromISO(date, { zone: props.timezone }).set({
        hour: hh,
        minute: mm,
        second: 0,
        millisecond: 0,
    });
    if (!composed.isValid) return;
    emit('update:modelValue', composed.toUTC().toISO());
    pickedDate.value = null;
}

function onDateChange(value) {
    const date = typeof value === 'string' ? value : null;

    if (!date) {
        pickedDate.value = null;
        if (props.modelValue) emit('update:modelValue', null);
        return;
    }

    if (displayedTime.value) {
        emitComposed(date, displayedTime.value);
        return;
    }

    // Date set, no time yet — track internally without dirtying the form.
    pickedDate.value = date;
}

function onTimeChange(time) {
    if (!time) {
        if (props.modelValue) emit('update:modelValue', null);
        pickedDate.value = null;
        return;
    }
    emitComposed(displayedDate.value, time);
}
</script>
